import { MutableRefObject } from 'react';
import { AndroidNotch } from '@awesome-cordova-plugins/android-notch';
import { StatusBar } from '@capacitor/status-bar';

import { SAFE_AREA_BOT, SAFE_AREA_TOP } from '@mopla/constants';

import type { IIntendCache } from '../useOverlayingWebView';

/**
 * Detects if the device is likely using gesture navigation vs button navigation
 * Returns appropriate fallback value for bottom safe area
 */
const getNavigationBarFallback = (): number => {
	try {
		// Check viewport height vs screen height
		// Gesture nav typically has smaller difference (~20-24px)
		// Button nav has larger difference (~48px)
		const viewportHeight = window.innerHeight;
		const screenHeight = window.screen.height;
		const diff = screenHeight - viewportHeight;

		// If difference is small, likely using gesture navigation
		if (diff > 0 && diff < 35) {
			return 20; // Gesture navigation
		}

		// If difference is large, likely using button navigation
		if (diff >= 35) {
			return 48; // Button navigation
		}

		// No navigation bar detected or can't determine
		// Modern devices default to gesture navigation
		return 20;
	} catch {
		return 20; // Safe default for modern devices
	}
};

/**
 * Gets the window insets from Android's WindowInsets API
 * This provides accurate system bar heights including gesture navigation
 */
const getWindowInsets = (): { top: number; bottom: number } | null => {
	try {
		// Try to get insets from CSS env() variables first (most reliable in Capacitor 7+)
		const rootStyle = getComputedStyle(document.documentElement);
		const envTop = rootStyle.getPropertyValue('--safe-area-inset-top');
		const envBottom = rootStyle.getPropertyValue('--safe-area-inset-bottom');

		if (envTop && envBottom) {
			return {
				top: parseFloat(envTop) || 0,
				bottom: parseFloat(envBottom) || 0,
			};
		}

		// Fallback: Try to read from CSS env() directly
		const tempDiv = document.createElement('div');
		tempDiv.style.position = 'fixed';
		tempDiv.style.top = 'env(safe-area-inset-top, 0px)';
		tempDiv.style.bottom = 'env(safe-area-inset-bottom, 0px)';
		document.body.appendChild(tempDiv);

		const computedStyle = getComputedStyle(tempDiv);
		const top = parseFloat(computedStyle.top) || 0;
		const bottom = parseFloat(computedStyle.bottom) || 0;

		document.body.removeChild(tempDiv);

		if (top > 0 || bottom > 0) {
			return { top, bottom };
		}
	} catch {
		// Silently fall back to AndroidNotch plugin
	}

	return null;
};

/**
 * @param style is a link on document.documentElement.style
 */
export const setAndroidPart = async (
	style: CSSStyleDeclaration,
	cache: MutableRefObject<IIntendCache>
) => {
	// First, enable edge-to-edge mode (must be done before getting insets)
	await StatusBar?.setOverlaysWebView({ overlay: true });
	await StatusBar?.setBackgroundColor({ color: '#00000000' }); // Transparent

	// Wait a bit for the system to adjust
	await new Promise((resolve) => setTimeout(resolve, 100));

	// Detect navigation bar type for fallback values
	const navBarFallback = getNavigationBarFallback();

	// Try to get insets from CSS env() variables (Capacitor 7+ with edge-to-edge)
	const windowInsets = getWindowInsets();

	if (windowInsets && (windowInsets.top > 0 || windowInsets.bottom > 0)) {
		const safeAreaTop = windowInsets.top || 20;
		const safeAreaBottom = windowInsets.bottom || navBarFallback;

		style.setProperty(SAFE_AREA_TOP, `${safeAreaTop}px`);
		cache.current.safeAreaTop = safeAreaTop;

		style.setProperty(SAFE_AREA_BOT, `${safeAreaBottom}px`);
		cache.current.safeAreaBottom = safeAreaBottom;

		return;
	}

	// Fallback to AndroidNotch plugin

	// Apply insets as css variables
	await AndroidNotch.getInsetTop().then(
		(px: number) => {
			// Use a default value of 20px for the status bar if cutouts are not reported reliably
			const safeArea = px || 20; // 20px is the default height of the Android status bar

			style.setProperty(SAFE_AREA_TOP, `${safeArea}px`);
			cache.current.safeAreaTop = safeArea;
		},
		() => {
			// Silently fail - defaults will be used
		}
	);

	await AndroidNotch.getInsetBottom().then(
		(px: number) => {
			// Use detected navigation type if AndroidNotch returns 0
			const safeAreaBottom = px || navBarFallback;

			style.setProperty(SAFE_AREA_BOT, `${safeAreaBottom}px`);
			cache.current.safeAreaBottom = safeAreaBottom;
		},
		() => {
			// Use detected navigation type as fallback
			style.setProperty(SAFE_AREA_BOT, `${navBarFallback}px`);
			cache.current.safeAreaBottom = navBarFallback;
		}
	);
};
